/* RECORDING AND RESOLVING A CANCELLATION (PRD 4).
 *
 * Two dialogs with deliberately different shapes, because they are two different
 * authorities and the screens should not let anybody forget which is which.
 *
 * RECORDING is nearly frictionless. Anybody may do it, it is heard rather than
 * decided, and the only required field is what the member actually said. There is no
 * picklist of reasons on purpose: a dropdown of three would turn somebody's words
 * into a category, and the whole point of this state is that the words print where
 * the score would otherwise be.
 *
 * RESOLVING is the opposite, and only the Owner may do it. It asks which of exactly
 * two endings happened and then insists on the half that ending owes: an exit reason
 * code when they left, what changed when they stayed. The model refuses a
 * half-written one; this screen never sends one, so that refusal is a backstop
 * rather than the experience.
 */
import { useState } from 'react';
import {
  canResolve, type CancellationResolution, type Member, type Role,
} from '@acrostic/model';
import { Field, Modal } from '../../components/Modal.js';
import { useSource } from '../../data/SourceProvider.js';
import { useToast } from '../../components/Toast.js';

/* THE SAME CODES A LEAD USES. Nothing exits without a reason code, on a member
   exactly as on a lead, and a second list here would be a second vocabulary for the
   one question the monthly owner review runs on. */
export const EXIT_REASONS = [
  'no reason given',
  'too difficult',
  'too expensive',
  'too far',
  'not interested',
  'no response',
  'moving away',
  'other',
] as const;

export function RecordCancellation({ m, onClose, onDone }: {
  m: Member; onClose: () => void; onDone: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [asked, setAsked] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!asked.trim() || busy) return;
    setBusy(true);
    try {
      await source.recordCancellation(m.id, { asked: asked.trim() });
      toast(`${m.firstName} is now at the top of the Owner's console`, 'good');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${m.name} asked about cancelling`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary t-label-medium"
            disabled={!asked.trim() || busy} onClick={() => void save()}>
            Record it
          </button>
        </>
      }
    >
      <p className="t-body-small muted">
        Record it the moment you hear it, whoever heard it. {m.firstName} moves to the
        top of the Manager and Owner consoles, stops being scored, and no campaign,
        sequence or reminder reaches them until the Owner closes it.
      </p>

      <Field
        label="What did they say"
        hint="In their words. This prints where their score would be, so what they actually said is worth more here than a category."
      >
        <textarea
          className="field" rows={3} value={asked} autoFocus
          placeholder="Asked what the notice period is, at the desk after class"
          onChange={(e) => setAsked(e.target.value)}
        />
      </Field>
    </Modal>
  );
}

export function ResolveCancellation({ m, by, roles, onClose, onDone }: {
  m: Member; by: string; roles: readonly Role[] | undefined;
  onClose: () => void; onDone: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [outcome, setOutcome] = useState<'left' | 'stayed' | null>(null);
  const [reasonCode, setReasonCode] = useState<string>(EXIT_REASONS[0]);
  const [whatChanged, setWhatChanged] = useState('');
  const [busy, setBusy] = useState(false);

  /* The source enforces this too. What is here is the courtesy, not the refusal:
     a screen hiding a control is never what makes an action impossible. */
  if (!canResolve(roles)) {
    return (
      <Modal
        title="Only the Owner can close this"
        onClose={onClose}
        footer={<button type="button" className="btn t-label-medium" onClick={onClose}>Close</button>}
      >
        <p className="t-body-small">
          {m.firstName} stays at the top of the console until the Owner has spoken to
          them and recorded how it ended. That is deliberate: it is the one
          conversation the product will not let anybody else quietly close.
        </p>
      </Modal>
    );
  }

  const ready = outcome === 'left' ? !!reasonCode
    : outcome === 'stayed' ? !!whatChanged.trim()
    : false;

  async function save() {
    if (!ready || !outcome || busy) return;
    setBusy(true);
    const resolution: CancellationResolution = outcome === 'left'
      ? { outcome, reasonCode, whatChanged: null, by }
      : { outcome, reasonCode: null, whatChanged: whatChanged.trim(), by };
    try {
      await source.resolveCancellation(m.id, resolution);
      toast(outcome === 'left'
        ? `${m.firstName} left. The reason is on the monthly review.`
        : `${m.firstName} stayed, and why is on their record.`, 'good');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`How did it end for ${m.firstName}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Not now</button>
          <button type="button" className="btn primary t-label-medium"
            disabled={!ready || busy} onClick={() => void save()}>
            Close it
          </button>
        </>
      }
    >
      <p className="t-body-small muted">
        It ends in one of two ways and both are written down. Nothing here expires on
        its own, so this is the only way it closes.
      </p>

      <div className="seg" role="group" aria-label="How it ended">
        <button type="button" className={'segbtn t-label-medium' + (outcome === 'stayed' ? ' on' : '')}
          onClick={() => setOutcome('stayed')}>They stayed</button>
        <button type="button" className={'segbtn t-label-medium' + (outcome === 'left' ? ' on' : '')}
          onClick={() => setOutcome('left')}>They left</button>
      </div>

      {outcome === 'stayed' && (
        <Field
          label="What changed"
          hint="A member who stays after asking is the most valuable case study the gym has, which is why this is required rather than optional."
        >
          <textarea
            className="field" rows={3} value={whatChanged} autoFocus
            placeholder="Moved to the 6am with Marcus, and we held the Tuesday slot"
            onChange={(e) => setWhatChanged(e.target.value)}
          />
        </Field>
      )}

      {outcome === 'left' && (
        <Field
          label="Exit reason"
          hint="The same codes a lead uses. The monthly owner review runs on these alone."
        >
          <select className="field" value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}>
            {EXIT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      )}
    </Modal>
  );
}
